import React, { useState } from 'react';
import {
  EmailType,
  EmailDripCampaignType,
  DripRequest,
  NewCampaignRequest,
} from '@scale/shared-types';
import { getFlowById } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import {
  OnboardingFlowStepNode,
  OnboardingFlowNodeType,
  OnboardingStepAttributeKey,
  getOnboardingStepAttributeMap,
} from '@scale/shared/onboardingControlPanel';

export interface DripsHook {
  drips: DripRequest[];
  setDrips: React.Dispatch<React.SetStateAction<DripRequest[]>>;
  deleteDrip: (index: number) => void;
  validateDrips: (
    campaignName?: string,
    signupFlowId?: string,
    greenhouseStageNames?: string[],
  ) => Promise<[boolean, string]>;
  validateTriggers: (
    campaignDetail: NewCampaignRequest,
    csvHeaders?: string[],
  ) => Promise<[boolean, string]>;
  countDrips: (type: EmailType) => number;
}

const useDrips = (initialDrips: DripRequest[]): DripsHook => {
  const [drips, setDrips] = useState<DripRequest[]>(initialDrips);

  const deleteDrip = (index: number) => {
    if (drips[index]._id) {
      drips[index].active = false;
      drips[index].deletedAt = new Date();
      setDrips([...drips]);
      return;
    } else {
      setDrips(drips.filter((_, i) => i !== index));
    }
  };

  const validateDrips = async (
    campaignName?: string,
    signupFlowId?: string,
    greenhouseStageNames?: string[],
  ): Promise<[boolean, string]> => {
    if (!campaignName) {
      return [false, 'Campaign fields are required'];
    }
    if (drips.length === 0) {
      return [false, 'At least one email is required'];
    }
    const activeDrips = drips.filter(
      drip => !drip.deletedAt && drip.active && drip.type === EmailType.Drip,
    );

    for (const drip of activeDrips) {
      if (
        !drip.email?.sendgridTemplateId ||
        !drip.email?.fromAddress ||
        !drip.email?.fromName ||
        !drip.email?.subject ||
        !drip.email?.body ||
        !drip.name
      ) {
        return [false, 'Missing drip email fields'];
      }
      if (!drip.tested) {
        return [
          false,
          'Please click "Send Test Email" and check that the contents of your email look okay',
        ];
      }
      if (!drip.daysInStage || drip.daysInStage <= 0) {
        return [false, 'Drip has invalid days in stage'];
      }
      const [validDrip, dripErrorMsg] = validateEmailBody(
        drip.email.subject,
        drip.email.body,
        EmailType.Drip,
        signupFlowId,
        greenhouseStageNames,
        drip.variableValues,
      );
      if (!validDrip) {
        return [false, dripErrorMsg];
      }
    }

    if (
      activeDrips
        .map(drip => drip.daysInStage)
        .filter((value, index, self) => self.indexOf(value) === index).length !== activeDrips.length
    ) {
      return [false, 'Drip Days in Stage values must be unique'];
    }

    return [true, ''];
  };

  const validateTriggers = async (
    campaignDetail: NewCampaignRequest,
    csvHeaders?: string[],
  ): Promise<[boolean, string]> => {
    const {
      type: campaignType,
      name: campaignName,
      onboardingFlowId,
      onboardingSteps,
    } = campaignDetail;

    if (!campaignType || !campaignName) {
      return [false, 'Campaign fields are required'];
    }
    if (drips.length === 0) {
      return [false, 'At least one email is required'];
    }
    const activeTriggers = drips.filter(
      trigger => !trigger.deletedAt && trigger.active && trigger.type === EmailType.Trigger,
    );
    if (campaignType === EmailDripCampaignType.CSV && activeTriggers.length === 0) {
      return [false, 'At least one email is required for a CSV campaign'];
    }

    if (activeTriggers.length > 0 && campaignType === EmailDripCampaignType.OCP) {
      if (!onboardingFlowId || !onboardingSteps || onboardingSteps.length === 0) {
        return [false, 'Onboarding flow and step are required for an OCP campaign'];
      }
      const [validOcpTrigger, ocpTriggerErrorMsg] = await validateOcpTriggers(
        onboardingFlowId,
        onboardingSteps.map(step => step.name),
      );
      if (!validOcpTrigger) {
        return [false, ocpTriggerErrorMsg];
      }
    }

    for (const trigger of activeTriggers) {
      const [validTrigger, triggerErrorMsg] = await validateTriggerFields(
        trigger,
        campaignType,
        csvHeaders,
        campaignDetail.signupFlowId,
        campaignDetail.greenhouseStageNames,
      );
      if (!validTrigger) {
        return [false, triggerErrorMsg];
      }
    }

    if (
      activeTriggers.length > 0 &&
      activeTriggers.reduce((sum, trigger) => sum + trigger.percentage!, 0) !== 100
    ) {
      return [
        false,
        campaignType !== EmailDripCampaignType.CSV
          ? 'Trigger percentages must sum to 100'
          : 'Email percentages must sum to 100',
      ];
    }

    return [true, ''];
  };

  // eslint-disable-next-line complexity
  const validateTriggerFields = async (
    trigger: DripRequest,
    campaignType: EmailDripCampaignType,
    csvHeaders?: string[],
    signupFlowId?: string,
    greenhouseStageNames?: string[],
  ): Promise<[boolean, string]> => {
    const isCSVCampaign = campaignType === EmailDripCampaignType.CSV;
    if (
      !trigger.email?.sendgridTemplateId ||
      !trigger.email?.fromAddress ||
      !trigger.email?.fromName ||
      !trigger.email?.subject ||
      !trigger.email?.body ||
      !trigger.name
    ) {
      return [false, !isCSVCampaign ? 'Missing trigger email fields' : 'Missing email fields'];
    }
    if (!trigger.tested) {
      return [
        false,
        'Please click "Send Test Email" and check that the contents of your email look okay',
      ];
    }
    if (trigger.percentage === undefined || trigger.percentage < 0 || trigger.percentage > 100) {
      return [false, !isCSVCampaign ? 'Invalid trigger percentage' : 'Invalid email percentage'];
    }
    if (isCSVCampaign && !csvHeaders) {
      return [false, 'CSV headers are required for CSV campaign'];
    }
    if (campaignType === EmailDripCampaignType.Greenhouse && !greenhouseStageNames) {
      return [false, 'Greenhouse stage(s) required for Greenhouse campaign'];
    }
    return validateEmailBody(
      trigger.email.subject,
      trigger.email.body,
      !isCSVCampaign ? EmailType.Trigger : undefined,
      signupFlowId,
      greenhouseStageNames,
      trigger.variableValues,
      csvHeaders,
    );
  };

  const validateEmailBody = (
    subject: string,
    body: string,
    emailType?: EmailType,
    signupFlowId?: string,
    greenhouseStageNames?: string[],
    variableValues: Record<string, string> = {},
    variables: string[] = ['email', 'first_name', 'last_name'],
  ): [boolean, string] => {
    let emailTypeName = '';
    switch (emailType) {
      case EmailType.Drip:
        emailTypeName = 'drip';
        break;
      case EmailType.Trigger:
        emailTypeName = 'trigger';
        break;
      default:
        emailTypeName = 'email';
    }

    const definedTemplateVariables = Object.keys(variableValues).filter(
      key => !!variableValues[key],
    );
    const combinedVariables = [...variables, ...definedTemplateVariables];
    // Checks every instance of {{variable}} against the available variables
    const variableRegex = /\{\{(.*?)\}\}/g;
    let match;
    while ((match = variableRegex.exec(body)) !== null) {
      const variable = match[1];
      if (!combinedVariables?.includes(variable)) {
        return [false, `{{${variable}}} is in ${emailTypeName} body but is not a variable`];
      }
    }
    while ((match = variableRegex.exec(subject)) !== null) {
      const variable = match[1];
      if (!combinedVariables?.includes(variable)) {
        return [false, `{{${variable}}} is in ${emailTypeName} subject but is not a variable`];
      }
    }

    if (signupFlowId) {
      // Check for correct signup flow following app.outlier.ai/en/expert/signup?signup_flow=
      const urlRegex = /app\.outlier\.ai\/en\/expert\/signup\?signup_flow=([0-9a-f-]*)/g;
      while ((match = urlRegex.exec(body)) !== null) {
        const urlSignupFlowId = match[1];
        if (signupFlowId !== urlSignupFlowId) {
          return [
            false,
            `Signup flow ${urlSignupFlowId} in ${emailTypeName} does not match provided signup flow`,
          ];
        }
      }

      let variableValuesContainSignupFlowId = false;
      for (const value of Object.values(variableValues)) {
        if (value.includes(signupFlowId)) {
          variableValuesContainSignupFlowId = true;
          break;
        }
      }

      // Check that offer stage emails include a signup flow ID
      if (
        greenhouseStageNames?.some(stageName => stageName.toLowerCase().includes('offer')) &&
        !body.includes(signupFlowId) &&
        !variableValuesContainSignupFlowId
      ) {
        return [false, `Offer stage ${emailTypeName}s must include a signup flow ID`];
      }

      // Check that anything matching the signup flow is preceded by app.outlier.ai/en/expert/signup?signup_flow=
      const expectedPrefix = 'app.outlier.ai/en/expert/signup?signup_flow=';
      const prefixLength = expectedPrefix.length;
      const prefixRegex = new RegExp(`(.{${prefixLength}})${signupFlowId}`, 'g');
      while ((match = prefixRegex.exec(body)) !== null) {
        const precedingCharacters = match[1];
        // Check if preceding characters match expected prefix
        if (precedingCharacters !== expectedPrefix) {
          return [false, `Invalid signup flow link in ${emailTypeName}`];
        }
      }
    }

    return [true, ''];
  };

  const validateOcpTriggers = async (
    onboardingFlowId: string,
    onboardingSteps: string[],
  ): Promise<[boolean, string]> => {
    const validTriggerNodeMap = getOnboardingStepAttributeMap(
      OnboardingStepAttributeKey.CanTriggerEmail,
    );

    const flow = await getFlowById(onboardingFlowId);
    if (!flow) {
      return [false, 'Could not find onboarding flow'];
    }

    const allNodeNames = new Set(flow.nodes.map(node => node.name));
    const missingSteps = [];
    for (const step of onboardingSteps) {
      if (!allNodeNames.has(step)) {
        missingSteps.push(step);
      }
    }
    if (missingSteps.length > 0) {
      return [false, `Could not find nodes ${missingSteps.join(', ')} in onboarding flow`];
    }

    const nodes = flow.nodes.filter(node => onboardingSteps.includes(node.name));
    for (const node of nodes) {
      if (
        node.type !== OnboardingFlowNodeType.Step ||
        !validTriggerNodeMap[(node as OnboardingFlowStepNode).stepType]
      ) {
        return [false, `Step ${node.name} does not support triggers`];
      }
    }

    return [true, ''];
  };

  const countDrips = (type: EmailType) => {
    return drips.filter(drip => !drip.deletedAt && drip.active && drip.type === type).length;
  };

  return { drips, setDrips, deleteDrip, validateDrips, validateTriggers, countDrips };
};

export default useDrips;
